import { useEffect, useState } from 'react';
import { getDocuments, GetDocumentsOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Search, FileText, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format, parseISO } from 'date-fns';

export default function ContentLibrarySection() {
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    getDocuments({}).then((d: any) => {
      setDocs(d.documents || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = docs.filter(d => {
    if (search && !(d.title || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (typeFilter !== 'all' && d.documentType !== typeFilter) return false;
    return true;
  });

  if (loading) {
    return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-foreground">Content Library</h2>
        <Button size="sm"><Upload className="w-4 h-4 mr-1" /> Upload</Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input placeholder="Search content…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="Policy">Policy</SelectItem>
            <SelectItem value="Certificate">Certificate</SelectItem>
            <SelectItem value="Contract">Contract</SelectItem>
            <SelectItem value="General">General</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Category</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground"><FileText className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No content found</p></td></tr>
            ) : filtered.map((d: any) => (
              <tr key={d.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 font-medium text-foreground">{d.title || 'Untitled'}</td>
                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{d.documentType || '—'}</td>
                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{d.documentCategory || '—'}</td>
                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                  {d.uploadDate ? format(parseISO(d.uploadDate), 'd MMM') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground mt-2">{filtered.length} items</p>
    </div>
  );
}
